import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { INR } from "@/lib/constants";
import { Star, MapPin, Phone, Download, CalendarCheck, Instagram, Sparkles, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (id) => `${BACKEND_URL}/api/public/portfolio/${id}`;

export default function PublicQuotePage() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/quotes/${publicId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [publicId]);

  if (error) return <Centered>This quotation link is no longer available.</Centered>;
  if (!data) return <Centered>Loading your quotation…</Centered>;

  const { quote, settings: s } = data;
  const studio = s.studio || {};
  const hidden = new Set(quote.hidden_sections || []);
  const ref = quote.reference;
  const isPartner = quote.mode === "partner";
  const payLink = studio.payment_link || "https://razorpay.me/@dejavumakeupbyvinni";
  const proof = [];
  if (s.google_rating) proof.push({ icon: Star, text: `${s.google_rating}`, sub: `Google${s.google_review_count ? ` · ${s.google_review_count}` : ""}` });
  if (s.socials?.weddings_done) proof.push({ text: `${s.socials.weddings_done}`, sub: "Weddings" });
  if (s.socials?.years_experience) proof.push({ text: `${s.socials.years_experience}`, sub: "Yrs Experience" });
  if (s.socials?.instagram_followers) proof.push({ icon: Instagram, text: `${s.socials.instagram_followers}`, sub: "Followers" });

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2A2421] pb-16 sm:pb-0" data-testid="public-quote" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* ===== Header ===== */}
      {!hidden.has("header") && (
      <header className="sticky top-0 z-30 bg-[#FAF9F6]/90 backdrop-blur border-b border-[#EAE7E0]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/dejavu-logo.png?v=3" alt={studio.name || "Dejavu Makeup by Vinni"} className="h-8 w-auto object-contain" />
          </div>
          {studio.phone && !isPartner && (
            <a href={`tel:${studio.phone}`} className="inline-flex items-center gap-1.5 text-xs text-[#5C5652] hover:text-[#C88270]"><Phone size={14} /> {studio.phone}</a>
          )}
        </div>
      </header>
      )}

      {/* ===== Hero ===== */}
      <section className="bg-[#2A2421] text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C88270]/20 blur-3xl" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C88270] mb-4">Personalised Quotation</div>
            <h1 className="font-serif text-4xl sm:text-6xl leading-[1.05]">{studio.name || "Dejavu Makeup"}</h1>
            {studio.tagline && <p className="text-sm sm:text-base text-white/70 mt-4 tracking-wide">{studio.tagline}</p>}
            <div className="mt-8 inline-flex flex-col border-l-2 border-[#C88270] pl-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Prepared for</span>
              <span className="font-serif text-2xl mt-1">{quote.client_name}</span>
              {quote.valid_until && <span className="text-xs text-white/40 mt-1">Valid until {quote.valid_until}</span>}
            </div>
            {ref && (
              <div className="mt-8 flex items-center gap-3" data-testid="pub-reference">
                {ref.logo && <img src={imgUrl(ref.logo.id)} alt={ref.name} className="w-12 h-12 object-contain bg-white/95 p-1 rounded" />}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">In association with</div>
                  <div className="font-serif text-lg leading-tight mt-0.5">{ref.company || ref.name}</div>
                  {ref.company && ref.name && <div className="text-xs text-white/50">{ref.name}</div>}
                </div>
              </div>
            )}
          </div>
          {s.artist_photo && (
            <div className="justify-self-center md:justify-self-end">
              <div className="relative">
                <div className="absolute inset-0 translate-x-3 translate-y-3 border border-[#C88270]/50" />
                <img src={imgUrl(s.artist_photo.id)} alt="Artist" className="relative w-56 h-72 sm:w-64 sm:h-80 object-cover" />
              </div>
              {s.about && (
                <p className="mt-6 max-w-xs text-sm text-white/70 leading-relaxed whitespace-pre-line" data-testid="pub-about">{s.about}</p>
              )}
            </div>
          )}
        </div>

        {/* Social proof band */}
        {proof.length > 0 && (
          <div className="border-t border-white/10 bg-black/20">
            <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {proof.map((p, i) => (
                <div key={p.sub || i} className="text-center">
                  <div className="flex items-center justify-center gap-1.5 font-serif text-2xl text-[#C88270]">
                    {p.icon && <p.icon size={18} className="text-[#C88270]" />}{p.text}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{p.sub}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16 space-y-16">
        {(s.section_order || ["galleries", "instagram", "pricing", "reviews", "terms"]).map((key) => {
          if (hidden.has(key)) return null;
          if (typeof key === "string" && key.startsWith("gallery:")) {
            const g = (s.galleries || []).find((x) => x.id === key.split(":")[1]);
            if (g && g.images?.length > 0) return (
              <section key={key} data-testid={`pub-gallery-${g.id}`}>
                <SectionTitle>{g.header || "Portfolio"}</SectionTitle>
                <div className="grid grid-cols-2 gap-3 sm:flex sm:gap-4">
                  {g.images.map((p) => (
                    <LazyImage key={p.id} id={p.id} alt="portfolio"
                      containerClass="aspect-[4/5] sm:aspect-auto sm:flex-1 sm:min-w-0 sm:h-64"
                      imgClass="hover:scale-[1.03] transition-transform duration-700" />
                  ))}
                </div>
              </section>
            );
            return null;
          }
          if (key === "why_choose_us") {
            const items = (s.why_choose_us?.items || []).filter((i) => (i.text || "").trim());
            if (items.length === 0) return null;
            return (
              <section key={key} data-testid="pub-why">
                <SectionTitle>{s.why_choose_us?.header || "Why Choose Us?"}</SectionTitle>
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  {items.map((it) => (
                    <div key={it.id} className="flex items-start gap-3 bg-white border border-[#EAE7E0] border-l-2 border-l-[#C88270] p-4 sm:p-5" data-testid={`pub-why-${it.id}`}>
                      <Sparkles size={18} className="text-[#C88270] shrink-0 mt-0.5" />
                      <span className="text-[15px] text-[#2A2421] leading-snug">{it.text}</span>
                    </div>
                  ))}
                </div>
              </section>
            );
          }
          if (key === "faqs") {
            const items = (s.faqs?.items || []).filter((i) => (i.question || "").trim());
            if (items.length === 0) return null;
            return (
              <section key={key} data-testid="pub-faq">
                <SectionTitle>{s.faqs?.header || "Frequently Asked Questions"}</SectionTitle>
                <div className="space-y-3">
                  {items.map((it) => <FaqItem key={it.id} item={it} />)}
                </div>
              </section>
            );
          }
          if (key === "galleries" && s.galleries?.some((g) => g.images?.length > 0)) return (
            <div key="galleries" className="space-y-12" data-testid="pub-galleries">
              {s.galleries.filter((g) => g.images?.length > 0).map((g) => (
                <section key={g.id}>
                  <SectionTitle>{g.header || "Portfolio"}</SectionTitle>
                  <div className="grid grid-cols-2 gap-3 sm:flex sm:gap-4">
                    {g.images.map((p) => (
                      <LazyImage key={p.id} id={p.id} alt="portfolio"
                        containerClass="aspect-[4/5] sm:aspect-auto sm:flex-1 sm:min-w-0 sm:h-64"
                        imgClass="hover:scale-[1.03] transition-transform duration-700" />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          );
          if (key === "instagram" && s.instagram?.screenshot) return (
            <section key="instagram" data-testid="pub-instagram" className="bg-white border border-[#EAE7E0] p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-8 items-center">
                <div className="mx-auto sm:mx-0 max-w-[280px]">
                  <LazyImage id={s.instagram.screenshot.id} alt="Instagram" natural containerClass="border border-[#EAE7E0]" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 text-[#C88270] mb-3"><Instagram size={20} /><span className="text-[10px] uppercase tracking-[0.3em]">Exclusive</span></div>
                  <h2 className="font-serif text-3xl text-[#2A2421] mb-2">Follow our authentic bride's Raw video</h2>
                  <p className="text-sm text-[#5C5652] mb-5">See real brides, behind-the-scenes and fresh work daily on Instagram.</p>
                  <a href={s.instagram.url} target="_blank" rel="noreferrer" data-testid="pub-instagram-follow"
                    className="inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors">
                    <Instagram size={16} /> Follow {s.instagram.handle}
                  </a>
                </div>
              </div>
            </section>
          );
          if (key === "pricing") return (
            <section key="pricing">
              <SectionTitle>Your Events & Quote</SectionTitle>
              <div className="bg-white border border-[#EAE7E0]">
                {(quote.events || []).map((e, i) => (
                  <div key={`${e.event_type}-${e.date}-${i}`} className="px-5 sm:px-6 py-5 border-b border-[#EAE7E0]" data-testid={`pub-event-${i}`}>
                    <div className="flex justify-between gap-4">
                      <div>
                        <div className="font-serif text-xl">{e.event_type}</div>
                        {e.service_name && <div className="text-[13px] text-[#C88270] mt-0.5">{e.service_name}</div>}
                        <div className="text-xs text-[#5C5652] mt-1.5">
                          {[e.date, (e.slots || []).join(", "), e.ready_by && `Ready by ${e.ready_by}`, e.location].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div className="font-serif text-lg whitespace-nowrap">{INR(e.price)}</div>
                    </div>
                    {(e.addons || []).map((a, j) => (
                      <div key={`${a.name}-${j}`} className="flex justify-between gap-4 mt-2 pl-4 text-sm text-[#5C5652]"><div>+ {a.name}</div><div>{INR(a.price)}</div></div>
                    ))}
                  </div>
                ))}
                {(quote.extras || []).filter((a) => Number(a.price) > 0).length > 0 && (
                  <div className="px-5 sm:px-6 py-5 border-b border-[#EAE7E0]" data-testid="pub-extras">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-2">Additional Charges</div>
                    {(quote.extras || []).filter((a) => Number(a.price) > 0).map((a, j) => (
                      <div key={a.key || `${a.name}-${j}`} className="flex justify-between gap-4 text-sm text-[#5C5652] mt-1.5"><div>{a.name}</div><div>{INR(a.price)}</div></div>
                    ))}
                  </div>
                )}
                <div className="px-5 sm:px-6 py-5 space-y-2">
                  {quote.calc.discount_amount > 0 && (
                    <>
                      <SumRow label="Subtotal" value={INR(quote.calc.subtotal)} />
                      <SumRow label="Discount" value={`- ${INR(quote.calc.discount_amount)}`} />
                    </>
                  )}
                  <SumRow label="Total" value={INR(quote.calc.total)} big />
                </div>
              </div>
              {quote.calc.advance_amount > 0 && (
                <div className="mt-4 bg-[#C88270]/10 border border-[#C88270]/30 px-5 sm:px-6 py-5 flex items-center justify-between gap-4" data-testid="pub-advance">
                  <div>
                    <div className="text-sm font-medium">Advance to block your date</div>
                    <div className="text-xs text-[#5C5652] mt-0.5">Balance {INR(quote.calc.balance)} payable on the event day</div>
                  </div>
                  <div className="font-serif text-3xl text-[#C88270]">{INR(quote.calc.advance_amount)}</div>
                </div>
              )}
              <div className="mt-3 flex items-start gap-2 text-xs text-[#B06A54] bg-[#FAF3F0] border border-[#E4C9C0] px-4 py-3" data-testid="pub-important-note">
                <Sparkles size={14} className="shrink-0 mt-0.5 text-[#C88270]" />
                <span><strong className="uppercase tracking-[0.12em] text-[10px]">Important</strong> · Dates are considered to be blocked only post advance payment.</span>
              </div>
              {quote.notes && quote.notes.trim() && (
                <div className="mt-3 bg-white border border-[#EAE7E0] px-5 sm:px-6 py-4" data-testid="pub-notes">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-1.5">Notes &amp; Special Requests</div>
                  <p className="text-sm text-[#2A2421] whitespace-pre-wrap leading-relaxed">{quote.notes}</p>
                </div>
              )}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <a href={`${BACKEND_URL}/api/public/quotes/${publicId}/pdf`} target="_blank" rel="noreferrer" data-testid="pub-download-pdf"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.2em] border border-[#2A2421] hover:bg-[#2A2421] hover:text-white transition-colors">
                  <Download size={16} /> Download PDF
                </a>
                <a href={payLink} target="_blank" rel="noreferrer" data-testid="pub-reserve"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.2em] bg-[#C88270] text-white hover:bg-[#2A2421] transition-colors">
                  <CalendarCheck size={16} /> Reserve my date{quote.calc.advance_amount > 0 && <> · {INR(quote.calc.advance_amount)}</>}
                </a>
              </div>
              {studio.phone && !isPartner && (
                <a href={`https://wa.me/${(studio.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Hi, I'd love to book! Quotation for ${quote.client_name}`)}`} target="_blank" rel="noreferrer" data-testid="pub-whatsapp"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#5C5652] hover:text-[#C88270]">
                  <Phone size={13} /> Have a question? Message us on WhatsApp
                </a>
              )}
            </section>
          );
          if (key === "reviews" && s.reviews?.length > 0) return (
            <section key="reviews">
              <SectionTitle>Loved by our brides</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4">
                {s.reviews.map((r, i) => (
                  <div key={`${r.name}-${i}`} className="bg-white border border-[#EAE7E0] px-5 py-5">
                    <div className="flex gap-0.5 mb-2">{Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} size={13} className="text-[#C88270] fill-[#C88270]" />)}</div>
                    <p className="text-[15px] italic leading-relaxed">“{r.text}”</p>
                    <div className="text-xs text-[#5C5652] mt-3 uppercase tracking-[0.15em]">— {r.name}</div>
                  </div>
                ))}
              </div>
            </section>
          );
          if (key === "terms" && (s.inclusions || s.terms || s.cancellation)) return (
            <section key="terms" className="grid sm:grid-cols-2 gap-8">
              {s.inclusions && <TextBlock title="What's Included" text={s.inclusions} />}
              {s.terms && <TextBlock title="Terms & Conditions" text={s.terms} />}
              {s.cancellation && <TextBlock title="Cancellation & Refund" text={s.cancellation} />}
            </section>
          );
          return null;
        })}
      </div>

      {/* ===== Footer ===== */}
      {!hidden.has("footer") && (
      <footer className="bg-[#2A2421] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 grid sm:grid-cols-2 gap-8">
          <div>
            <img src="/dejavu-logo-white.png?v=3" alt={studio.name || "Dejavu Makeup by Vinni"} className="h-10 w-auto object-contain mb-3" />
            {studio.tagline && <p className="text-sm text-white/50">{studio.tagline}</p>}
          </div>
          <div className="text-sm text-white/70 space-y-2">
            {studio.address && <div className="flex items-start gap-2"><MapPin size={15} className="mt-0.5 shrink-0 text-[#C88270]" />{studio.address}</div>}
            {studio.phone && !isPartner && <div className="flex items-center gap-2"><Phone size={15} className="text-[#C88270]" />{studio.phone}</div>}
            {s.socials?.instagram_handle && <div className="flex items-center gap-2"><Instagram size={15} className="text-[#C88270]" />{s.socials.instagram_handle}</div>}
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between text-[11px] text-white/40">
            <span className="inline-flex items-center gap-1.5"><Sparkles size={12} className="text-[#C88270]" /> Crafted with love</span>
            <span>© {new Date().getFullYear()} {studio.name || "Dejavu Makeup"}</span>
          </div>
        </div>
      </footer>
      )}

      {/* ===== Mobile sticky Reserve CTA ===== */}
      <a href={payLink} target="_blank" rel="noreferrer" data-testid="pub-reserve-mobile"
        className="sm:hidden fixed inset-x-0 bottom-0 z-40 flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.2em] bg-[#C88270] text-white shadow-[0_-6px_24px_rgba(42,36,33,0.18)]">
        <CalendarCheck size={16} /> Reserve my date{quote.calc.advance_amount > 0 && <> · {INR(quote.calc.advance_amount)}</>}
      </a>
    </div>
  );
}

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-[#5C5652] text-sm px-6 text-center">{children}</div>
);
const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#EAE7E0]" data-testid={`pub-faq-${item.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 sm:px-6">
        <span className="font-serif text-lg sm:text-xl text-[#2A2421]">{item.question}</span>
        <ChevronDown size={18} className={`text-[#C88270] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className="grid transition-all duration-300 ease-out" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
        <div className="overflow-hidden">
          <p className="px-5 sm:px-6 pb-5 text-[15px] text-[#5C5652] leading-relaxed whitespace-pre-line">{item.answer}</p>
        </div>
      </div>
    </div>
  );
};

const LazyImage = ({ id, alt = "", containerClass = "", imgClass = "", natural = false }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`relative overflow-hidden bg-[#EAE7E0] ${containerClass}`} data-testid={`lazy-img-${id}`}>
      <img src={`${imgUrl(id)}?w=24`} alt="" aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg"
        style={{ opacity: loaded ? 0 : 1, transition: "opacity 700ms ease" }} />
      <img src={imgUrl(id)} alt={alt} loading="lazy" decoding="async" onLoad={() => setLoaded(true)}
        className={`relative w-full object-cover ${natural ? "h-auto" : "h-full"} ${imgClass}`}
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 700ms ease" }} />
    </div>
  );
};
const SectionTitle = ({ children, small }) => (
  <div className="mb-6">
    <div className="w-10 h-px bg-[#C88270] mb-3" />
    <h2 className={`font-serif text-[#2A2421] ${small ? "text-2xl" : "text-3xl sm:text-4xl"}`}>{children}</h2>
  </div>
);
const SumRow = ({ label, value, big }) => (
  <div className="flex justify-between items-center">
    <span className={big ? "font-medium text-base" : "text-[#5C5652] text-sm"}>{label}</span>
    <span className={big ? "font-serif text-2xl text-[#C88270]" : "text-sm"}>{value}</span>
  </div>
);
const TextBlock = ({ title, text }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#C88270] mb-2">{title}</div>
    <p className="text-sm text-[#5C5652] leading-relaxed whitespace-pre-line">{text}</p>
  </div>
);
